'use client';
import cn from 'classnames';
import { useLocale } from 'next-intl';
import { useEffect, useState } from 'react';

import CardTop from './atoms/CardTop';

import { LazyLoader } from '@components/Lazy';
import { toastError } from '@components/root/NotificationProxy';
import { fetchApi } from '@utils/api/client';
import { Author } from 'types/author';

type SortOption = 'date' | 'name' | 'posts_count';

interface AuthorsListProps {
  initialAuthors: Author[];
  labels: {
    error: string;
    sortBy: string;
    authors: string;
    sortOptions: {
      [key in SortOption]: string;
    };
    emptyContent: string;
    authorCard: {
      guestName: string;
      userPosts: string;
    };
  };
}

const AuthorsList = ({ initialAuthors, labels }: AuthorsListProps) => {
  const locale = useLocale();
  const [authorList, setAuthorList] = useState(initialAuthors);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [sortBy, setSortBy] = useState<SortOption>('posts_count');
  const [isFinished, setIsFinished] = useState(false);

  const fetchAuthors = async () => {
    if (isLoading) {
      return;
    }

    setIsLoading(true);

    try {
      const authorsResponse = await fetchApi<Author[]>(`${process.env.NEXT_PUBLIC_API}/users/authors`, {
        params: {
          limit: 10,
          awardedAuthor: false,
          langName: locale,
          page,
          sortBy,
          order: sortBy === 'name' ? 'asc' : 'desc',
        },
      });

      if (!authorsResponse.data.length) {
        setIsFinished(true);
      }

      setAuthorList((current) => [...current, ...authorsResponse.data]);
    } catch (e) {
      toastError(labels.error);
    } finally {
      setIsLoading(false);
    }
  };

  const onLazyLoad = (isVisible: boolean) => {
    if (isVisible && !isLoading) {
      setPage((p) => p + 1);
    }
  };

  const onSetSortBy = (option: SortOption) => {
    if (isLoading) {
      return;
    }

    setSortBy(option);
    setPage(0);
    setAuthorList([]);
  };

  useEffect(() => {
    fetchAuthors();
  }, [page, sortBy]);

  return (
    <div className="">
      <div className="container mx-auto py-10 lg:px-10">
        <div className="px-6 text-center">
          <h2 className="text-3xl font-bold text-premium-dark dark:text-orange-500">{labels.authors}</h2>
          <div className="mt-5 flex items-center justify-center">
            {labels.sortBy}
            <div className="ml-4 hidden md:block">
              {Object.entries<string>(labels.sortOptions).map((item) => {
                const [sortByOption, sortByLabel] = item;
                return (
                  <button
                    key={sortByOption}
                    className={cn('mx-1 rounded-full border-none bg-none px-4 py-2 text-base font-semibold shadow-md', {
                      'bg-premium-dark text-white ': sortBy === sortByOption,
                    })}
                    onClick={() => onSetSortBy(sortByOption as SortOption)}
                    disabled={isLoading}
                  >
                    {sortByLabel}
                  </button>
                );
              })}
            </div>
            <div className="md:hidden">
              <select defaultValue={sortBy} className="ml-2.5 w-52" disabled={isLoading}>
                {Object.entries<string>(labels.sortOptions).map((item) => (
                  <option
                    key={item[0]}
                    onClick={() => onSetSortBy(item[0] as SortOption)}
                    disabled={isLoading}
                    value={item[0]}
                  >
                    {item[1]}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className="mt-8 grid grid-cols-1 gap-5 sm:mt-5 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
          {authorList.map((author, id) => (
            <CardTop size="small" author={author} key={'author' + author.slug + id} />
          ))}
          {!isFinished && <LazyLoader onChange={onLazyLoad} />}
          {authorList.length === 0 && !isLoading && <div className="p-5">{labels.emptyContent}</div>}
        </div>
      </div>
    </div>
  );
};

export default AuthorsList;
